import { atom } from 'jotai';
import { competitionLookupAtom } from '@/atoms/queries/competitions';
import { fetchForDefaultTeam } from './fetchForDefaultTeam';

export type HomeDefaultTeamCompetition = { datahubId: number; classicId: number; name: string };

type CompetitionIds = { datahubId: number; classicId: number };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const toId = (value: unknown) => (typeof value === 'number' && value > 0 ? value : null);

// Only the competition is needed from the navigation ids. A team with none has ids of 0, which is not an error.
const parseCompetitionIds = (response: unknown): CompetitionIds | null => {
  if (!isRecord(response) || !isRecord(response.competition)) {
    throw new Error('Unexpected navigation-link-ids response: no competition');
  }
  const datahubId = toId(response.competition.datahub_id);
  const classicId = toId(response.competition.iq_id);
  return datahubId && classicId ? { datahubId, classicId } : null;
};

// The competition of the default team's latest game (the API prefers leagues): its name, its id for the logo, and
// its Classic id for the league table, which is the only competition page there is. The side nav's navigation ids
// follow the team in context, which on Home may be the last team visited, so Home asks for the default team's own.
// Unknown or failed lookups give null.
export const homeDefaultTeamCompetitionAtom = atom(async (get): Promise<HomeDefaultTeamCompetition | null> => {
  const [competitionIds, competitionLookup] = await Promise.all([
    fetchForDefaultTeam(
      get,
      'homeDefaultTeamNavigationLinkIds',
      teamId => `/team/${teamId}/navigation-link-ids`,
      parseCompetitionIds,
    ),
    get(competitionLookupAtom),
  ]);
  if (!competitionIds) return null;

  const name = competitionLookup.get(competitionIds.datahubId)?.name;
  return name ? { ...competitionIds, name } : null;
});
